import React, { useMemo, useEffect, useState } from "react";
import { AdminToolbar } from "@opendash/ui";
import { useSource, useTranslation, AdminLayout } from "@opendash/core";
import { Icon } from "@opendash/icons";
import { Parse } from "@openinc/parse-hooks";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  Table,
  Button,
  Space,
  Typography,
  Tooltip,
  notification,
  Popconfirm,
} from "antd";
import CreateServiceModal from "./GreisServiceNewModal";

const { Title } = Typography;
dayjs.extend(utc);

type NotificationType = "success" | "info" | "warning" | "error";

interface ServiceObject {
  id: string;
  number: string;
  name: string;
  op: number;
  services: any;
  last_ohours: any;
  last_hours_fired: number;
  last_op_hours_fired: number;
  last_services: any;
  currentService: any;
  order: number;
  nextServiceOP?: number;
  nextServiceHOUR?: any[];
}

export default function GreisServiceView({ spsid }: { spsid: string }) {
  const t = useTranslation();
  const [source] = useSource();
  const [api, contextHolder] = notification.useNotification();
  const [visible, setVisible] = useState<boolean>(false);
  const [edit, setEdit] = useState<boolean>(false);
  const [editButtonClicked, setEditButtonClicked] = useState<boolean>(false);
  const [currentSPSID, setCurrentSPSID] = useState<string>(source.tag);
  const [changes, setChanges] = useState<boolean>(false);
  const [allServices, setAllServices] = useState<ServiceObject[]>([]);

  const loadServices = async () => {
    try {
      const allServicePuffer: ServiceObject[] = [];
      const ServiceEntry = Parse.Object.extend("HG_Service");
      const query = new Parse.Query(ServiceEntry).limit(1000000);
      query.include("source");
      const results = await query.find();

      results.forEach(async (result) => {
        if (
          result.get("service_body").length > 0 &&
          (!result.get("last_services") ||
            result.get("last_services").length < 1 ||
            !result.get("last_services")[result.get("last_services").length - 1]
              .active)
        ) {
          const serviceName = result.get("source").get("name");
          const serviceNumber =
            result.get("source").get("machineNumber") ||
            result.get("source").get("tag");

          const lastService = result.get("last_services")?.slice(-1)[0] || null;
          const serviceObject: ServiceObject = {
            id: result.get("source").get("tag"),
            number: serviceNumber,
            name: serviceName,
            op: parseInt(result.get("op_hours")),
            services: result.get("service_body"),
            last_ohours: result.get("last_ohours"),
            last_hours_fired: result.get("last_hours_fired"),
            last_op_hours_fired: result.get("last_op_hours_fired"),
            last_services: lastService,
            order: Infinity,
            currentService: undefined,
          };

          result.get("service_body").forEach((e) => {
            if (e.type === "hour" && e.active === 1) {
              try {
                serviceObject.order = dayjs(
                  lastService?.meta?.at_value || e.start
                )
                  .add(e.value, "month")
                  .valueOf();
              } catch {
                serviceObject.order = new Date(e.start).getTime();
              }
              serviceObject.nextServiceHOUR = getNextServiceDateForHOUR(
                result.get("last_hours_fired") > 1000000000000
                  ? dayjs(result.get("last_hours_fired")).startOf("day")
                  : dayjs(e.start),
                parseInt(e.value)
              );
            }

            if (e.type === "op" && e.active === 1) {
              const delay = result.get("last_op_hours_fired")
                ? parseInt(result.get("last_op_hours_fired")) +
                  parseInt(e.value) -
                  parseInt(result.get("op_hours"))
                : e.start - parseInt(result.get("op_hours"));
              serviceObject.order += delay;
              serviceObject.nextServiceOP = getNextServiceDateForOP(
                e.start,
                result.get("op_hours"),
                parseInt(e.value),
                result.get("last_op_hours_fired")
              );
            }
          });
          allServicePuffer.push(serviceObject);
        }
      });

      setAllServices(allServicePuffer);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    loadServices();
  }, [visible, changes]);

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    description: string
  ) => {
    api[type]({
      message: title,
      description,
    });
  };

  const getNextServiceDateForOP = (
    start: number,
    currentOP: number,
    intervall: number,
    last_op_hours_fired: number
  ): number => {
    if (currentOP < start) {
      return start - currentOP;
    } else {
      if (currentOP > start && start > last_op_hours_fired) {
        return start - currentOP;
      } else {
        return last_op_hours_fired + intervall - currentOP;
      }
    }
  };

  const getNextServiceDateForHOUR = (
    dateStart: dayjs.Dayjs,
    intervall: number
  ): any[] => {
    const today = dayjs().startOf("day").hour(0);
    let nextServiceDate: dayjs.Dayjs;

    if (today.isBefore(dateStart)) {
      const daysUntilNextService = dateStart.diff(today, "day");
      if (daysUntilNextService < 1) {
        return [1, dateStart];
      } else {
        return [daysUntilNextService, dateStart];
      }
    } else {
      switch (intervall) {
        case 1:
          nextServiceDate = dateStart.add(1, "month");
          break;
        case 2:
          nextServiceDate = dateStart.add(3, "month");
          break;
        case 3: // Halbjährlich (alle 6 Monate)
          nextServiceDate = dateStart.add(6, "month");
          break;
        case 4: // Jährlich
          nextServiceDate = dateStart.add(1, "year");
          break;
        case 5: // Alle 2 Jahre
          nextServiceDate = dateStart.add(2, "year");
          break;
        case 99: // Jeden Tag
          nextServiceDate = dateStart.add(1, "day");
          break;
        default:
          throw new Error("Unbekanntes Intervall");
      }

      if (!nextServiceDate || !nextServiceDate.isValid()) {
        throw new Error("nextServiceDate ist ungültig");
      }
      const daysUntilNextService = nextServiceDate.diff(today, "day");
      if (daysUntilNextService < 1) {
        return [1, nextServiceDate];
      } else {
        return [daysUntilNextService, nextServiceDate];
      }
    }
  };

  const getServiceTypes = (data) => {
    const hasOpActive = data.some(
      (item) => item.type === "op" && item.active === 1
    );
    const hasHourActive = data.some(
      (item) => item.type === "hour" && item.active === 1
    );

    if (hasOpActive && hasHourActive) {
      return t("greis:serviceview.component.type_both");
    } else if (hasOpActive) {
      return t("greis:serviceview.component.type_operating_hours");
    } else if (hasHourActive) {
      return t("greis:serviceview.component.type_time");
    }

    return "";
  };

  const deleteEntry = async (id: string) => {
    try {
      const ServiceEntry = Parse.Object.extend("HG_Service");
      const query = new Parse.Query(ServiceEntry).limit(1000000);
      const sourceQuery = new Parse.Query("OD3_Source").limit(1000000);
      sourceQuery.equalTo("tag", id);
      query.include("source");
      query.matchesQuery("source", sourceQuery);

      const result = await query.first();
      if (result) {
        const name = result?.get("source").get("name");
        result.set("service_body", []);
        result.set("last_services", []);
        result.set("last_hours_fired", 0);
        result.set("last_op_hours_fired", 0);
        result.set("last_ohours", 0);
        result.set("last_hours", 0);
        await result.save();
        openNotificationWithIcon(
          "info",
          t("greis:serviceview.component.info_delete_success"),
          t("greis:serviceview.component.info_delete_success_text")
        );
      } else {
        openNotificationWithIcon(
          "error",
          t("greis:serviceview.component.info_delete_error"),
          t("greis:serviceview.component.info_delete_error_text")
        );
      }
    } catch (error: any) {
      openNotificationWithIcon(
        "error",
        t("greis:serviceview.component.info_delete_error"),
        t("greis:serviceview.component.info_delete_error_text") + " - " + error
      );
    }
    loadServices();
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "number",
      key: "number",
      align: "center",
      responsive: ["lg"],
    },
    {
      title: t("greis:serviceview.component.table_source"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("greis:serviceview.component.table_type"),
      dataIndex: "services",
      key: "services",
      align: "center",
      render: getServiceTypes,
      responsive: ["md"],
    },

    {
      title: t("greis:serviceview.component.table_current_op"),
      dataIndex: "op",
      align: "center",
      key: "betriebsstunden",
      render: (text) =>
        isNaN(text)
          ? "-"
          : text == 0
          ? "-"
          : text + " " + t("greis:serviceview.component.table_content_hours"),
      responsive: ["md"],
    },
    {
      title: t("greis:serviceview.component.table_last_maintain"),
      dataIndex: "letzteWartung",
      align: "center",
      key: "letzteWartung",
      render: (text, record) => {
        if (record.last_services == null) {
          return t("greis:serviceview.component.table_content_no_maintain");
        } else {
          const letzteWartung = dayjs(record.last_services.meta.when);
          const quittiertAm = dayjs(record.last_services.ack.when);
          return `${letzteWartung.format("DD.MM.YYYY")}  (${t(
            "greis:serviceview.component.table_content_ack_at"
          )}: ${quittiertAm.format("DD.MM.YYYY")})`;
        }
      },
      responsive: ["lg"], //WIr hatten 670 Betriebstunden
    },
    {
      title: t("greis:serviceview.component.table_next_maintain"),
      dataIndex: "nextService",
      key: "nextService",
      align: "center",
      render: (_, record) => {
        if (record.nextServiceHOUR) {
          return (
            <>
              {record.nextServiceHOUR &&
                `${record.nextServiceHOUR[0]} ${
                  record.nextServiceHOUR[0] > 1
                    ? t("greis:serviceview.multi")
                    : t("greis:serviceview.single")
                } ${t(
                  "greis:serviceview.component.table_content_at"
                )} ${record.nextServiceHOUR[1].format("DD.MM.YYYY")}`}
              {record.nextServiceHOUR[0] && record.nextServiceOP && " | "}
              {record.nextServiceOP &&
                record.nextServiceOP >= 0 &&
                `${record.nextServiceOP} ${t(
                  "greis:serviceview.component.type_operating_hours"
                )}`}
              {record.nextServiceOP &&
                record.nextServiceOP < 0 &&
                t("greis:serviceview.component.table_content_due")}
            </>
          );
        } else if (record.nextServiceOP) {
          return (
            <>
              {record.nextServiceOP &&
                record.nextServiceOP >= 0 &&
                `${record.nextServiceOP} ${t(
                  "greis:serviceview.component.type_operating_hours"
                )}`}
              {record.nextServiceOP &&
                record.nextServiceOP < 0 &&
                t("greis:serviceview.component.table_content_due")}
            </>
          );
        }
      },
    },
    {
      title: t("greis:serviceview.component.table_action"),
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title={t("greis:serviceview.component.table_edit_button")}>
            <Button
              icon={<Icon icon="fa:pen" />}
              onClick={() => {
                setEditButtonClicked(true);
                setCurrentSPSID(record.id);
                setEdit(true);
                setVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title={t("greis:serviceview.component.table_delete_button")}>
            <Popconfirm
              title={t("greis:serviceview.component.table_delete_button")}
              description={t("greis:serviceview.component.table_delete_text")}
              onConfirm={() => deleteEntry(record.id)}
              okText={t("greis:serviceview.component.table_delete_button")}
              cancelText={t("greis:serviceview.component.table_delete_cancel")}
            >
              <Button icon={<Icon icon="fa:trash" />} danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <AdminLayout>
      {contextHolder}
      <AdminToolbar
        title={t("greis:serviceview.label")}
        description={t("greis:serviceview.description_all")}
        actions={[
          <Button
            key="1"
            type="primary"
            onClick={() => {
              setEdit(true), setVisible(true);
            }}
          >
            {t("greis:serviceview.component.new_button")}
          </Button>,
        ]}
      />
      <div className="od-page-main">
        <Title level={5}>{t("greis:serviceview.component.title")}</Title>
        <Table dataSource={allServices} columns={columns} pagination={false} />
      </div>
      <CreateServiceModal
        editButtonClicked={editButtonClicked}
        edit={edit}
        changes={changes}
        visible={visible}
        onCancel={() => {
          setEdit(false);
          setEditButtonClicked(false);
          setVisible(false);
        }}
        spsid={currentSPSID}
      />
    </AdminLayout>
  );
}
